import { useEffect, useMemo, useState } from "react"
import { LogError } from "../../../helpers/logger"

const DEFAULT_INVALID_MESSAGE = "This authentication link is invalid or expired."

export default function useAuthActionLink({
  expectedMode,
  verifyCode,
  invalidLinkMessage = DEFAULT_INVALID_MESSAGE,
}) {
  // Parse query params once so auth-link handling is deterministic per page load.
  const { mode, oobCode } = useMemo(() => {
    const searchParams = new URLSearchParams(window.location.search)
    return {
      mode: searchParams.get("mode") || "",
      oobCode: searchParams.get("oobCode") || "",
    }
  }, [])

  const [isVerifying, setIsVerifying] = useState(true)
  const [isVerified, setIsVerified] = useState(false)
  const [verifiedPayload, setVerifiedPayload] = useState(null)
  const [verificationError, setVerificationError] = useState(null)

  useEffect(() => {
    let cancelled = false

    const verifyActionLink = async () => {
      setIsVerifying(true)
      setIsVerified(false)
      setVerifiedPayload(null)
      setVerificationError(null)

      // Reject links that are missing required Firebase action parameters.
      if (!oobCode || mode !== expectedMode || typeof verifyCode !== "function") {
        // Log only safe diagnostics and avoid raw query/token values.
        LogError("Auth action link validation failed before verification", {
          expectedMode,
          mode,
          hasOobCode: Boolean(oobCode),
          hasVerifyCode: typeof verifyCode === "function",
        })
        setVerificationError(invalidLinkMessage)
        setIsVerifying(false)
        return
      }

      try {
        const payload = await verifyCode(oobCode)
        if (cancelled) return

        setVerifiedPayload(payload)
        setIsVerified(true)
      } catch (error) {
        if (cancelled) return

        LogError("Auth action link verification failed", {
          expectedMode,
          mode,
          hasOobCode: Boolean(oobCode),
          errorCode: error?.code || "unknown",
          errorMessage: error?.message || "unknown",
        })
        setVerificationError(invalidLinkMessage)
      } finally {
        if (!cancelled) {
          setIsVerifying(false)
        }
      }
    }

    verifyActionLink()

    return () => {
      cancelled = true
    }
  }, [expectedMode, invalidLinkMessage, mode, oobCode, verifyCode])

  return {
    mode,
    oobCode,
    isVerifying,
    isVerified,
    verifiedPayload,
    verificationError,
  }
}
